import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../../lib/api";
import { toast } from "sonner";
import axios from "axios";

import { format } from "date-fns";
import { ArrowUpDown, Download, FileText } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Badge } from "src/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { DateFormate } from "../../../../lib/utils";
import CardSkeleton from "../../../../components/common/CardSkeleton";

export default function Invoices() {
  const { language } = useLanguage();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortDirection, setSortDirection] = useState("desc");
  const [activeTab, setActiveTab] = useState("all");

  const fetchInvoices = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/student/payments`);
      setInvoices(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast({ title: "Error", description: result });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchInvoices();
  }, []);

  const toggleSort = () => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
  });

  const filteredInvoices = sortedInvoices.filter((invoice) => {
    if (activeTab === "all") return true;
    if (activeTab === "paypal") return invoice.paypal_id !== null;
    if (activeTab === "myfatoorah") return invoice.myfatoorah_id !== null;
    return true;
  });

  const downloadPdf = (invoice) => {
    const isPayPal = invoice.paypal_id !== null;
    const paymentDetails = isPayPal ? invoice.paypal : invoice.myfatoorah;

    const doc = new jsPDF();

    const svgStr = isPayPal ? PaypalSVG : MyFatoorahSVG;
    // Add logo
    if (isPayPal) {
      // doc.addImage(svgStr, "SVG", 15, 15, 50, 15);
      doc.setTextColor(0, 112, 186); // PayPal blue
    } else {
      // doc.addImage(svgStr, "SVG", 15, 15, 50, 15);
      doc.setTextColor(0, 168, 133); // MyFatoorah green
    }

    // Add title
    doc.setFontSize(20);
    doc.text(`Invoice #${invoice.id}`, 105, 20, { align: "center" });

    // Add date
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${DateFormate(invoice.created_at)}`, 105, 30, {
      align: "center",
    });

    // Reset text color
    doc.setTextColor(0, 0, 0);

    // Add payment method
    doc.setFontSize(14);
    doc.text(`Payment Method: ${isPayPal ? "PayPal" : "MyFatoorah"}`, 20, 50);

    // Add invoice details
    autoTable(doc, {
      startY: 60,
      head: [["Detail", "Value"]],
      body: [
        ["Invoice ID", `#${invoice.id}`],
        ["User ID", `#${invoice.user_id}`],
        ["Payer Name", paymentDetails?.payer_name || "N/A"],
        ["Payer Email", paymentDetails?.payer_email || "N/A"],
        ["Amount", `$${invoice.amount.toFixed(2)}`],
        ["Status", invoice.status],
        [
          isPayPal ? "Transaction ID" : "MyFatoorah Invoice ID",
          isPayPal
            ? paymentDetails?.transaction_id
            : paymentDetails?.invoice_id,
        ],
        ["Date", DateFormate(invoice.created_at)],
      ],
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text(
        "Thank you for your payment. If you have any questions, please contact support.",
        105,
        285,
        {
          align: "center",
        }
      );
    }

    // Save the PDF
    doc.save(`Invoice-${invoice.id}.pdf`);
  };

  return (
    <div className="space-y-6" dir={language === "en" ? "ltr" : "rtl"}>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-2">
          <FileText className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold">Invoices</h1>
        </div>
        <Button variant="outline" onClick={toggleSort} className="gap-2">
          <span>Sort by Date</span>
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all" className="mb-8" onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="paypal">PayPal</TabsTrigger>
          <TabsTrigger value="myfatoorah">MyFatoorah</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Empty state or invoices grid */}
      {loading ? (
        <CardSkeleton />
      ) : filteredInvoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold">No invoices found.</h2>
          <p className="text-sm text-muted-foreground">
            You have no invoices to display.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInvoices.map((invoice) => {
            const isPayPal = invoice.paypal_id !== null;
            const paymentDetails = isPayPal
              ? invoice.paypal
              : invoice.myfatoorah;

            return (
              <Card
                key={invoice.id}
                className="overflow-hidden flex flex-col justify-between p-0"
              >
                <div
                  className={`h-2 w-full ${
                    isPayPal ? "bg-[#0070ba]" : "bg-[#00a885]"
                  }`}
                />
                <CardHeader className="pb-2 relative">
                  <div className="absolute right-6 top-6">
                    <Badge
                      variant={
                        invoice.status === "completed" ? "success" : "default"
                      }
                      className={`${
                        isPayPal
                          ? "bg-blue-100 text-blue-800 hover:bg-blue-100"
                          : "bg-green-100 text-green-800 hover:bg-green-100"
                      }`}
                    >
                      {invoice.status.charAt(0).toUpperCase() +
                        invoice.status.slice(1)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    {isPayPal ? (
                      <div className="w-16 h-16 flex items-center justify-center">
                        {PaypalSVG}
                      </div>
                    ) : (
                      <div className="w-16 h-16 flex items-center justify-center">
                        {MyFatoorahSVG}
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">
                        Invoice #{invoice.id}
                      </CardTitle>
                      <div className="text-sm text-muted-foreground">
                        {DateFormate(invoice.created_at)}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground">
                          Payer Name
                        </div>
                        <div className="font-medium">
                          {paymentDetails?.payer_name}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">
                          Amount
                        </div>
                        <div className="font-medium text-lg">
                          ${invoice.amount.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-muted-foreground">
                        Payer Email
                      </div>
                      <div className="font-medium">
                        {paymentDetails?.payer_email}
                      </div>
                    </div>

                    {isPayPal ? (
                      <div>
                        <div className="text-sm text-muted-foreground">
                          Transaction ID
                        </div>
                        <div className="font-medium text-xs truncate">
                          {invoice.paypal?.transaction_id}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm text-muted-foreground">
                          MyFatoorah Invoice ID
                        </div>
                        <div className="font-medium">
                          {invoice.myfatoorah?.invoice_id}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="p-0">
                  <Button
                    className={`w-full ${
                      isPayPal
                        ? "bg-[#0070ba] hover:bg-[#003087] text-white"
                        : "bg-[#00a885] hover:bg-[#008c6f] text-white"
                    }`}
                    onClick={() => downloadPdf(invoice)}
                  >
                    <Download className="h-4 w-4 ms-2" />
                    Download as PDF
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PaypalSVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 124 33"
    className="w-16 h-auto"
  >
    <path
      d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.97-1.142-2.694-1.746-4.985-1.746zM47 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z"
      fill="#253B80"
    />
    <path
      d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482z"
      fill="#179BD7"
    />
    <path
      d="M7.266 29.154l.523-3.322-1.165-.027H1.061L4.927 1.292a.316.316 0 0 1 .314-.268h9.38c3.114 0 5.263.648 6.385 1.927.526.6.861 1.227 1.023 1.917.17.724.173 1.589.007 2.644l-.012.077v.676l.526.298a3.69 3.69 0 0 1 1.065.812c.45.513.741 1.165.864 1.938.127.795.085 1.741-.123 2.812-.24 1.232-.628 2.305-1.152 3.183a6.547 6.547 0 0 1-1.825 2c-.696.494-1.523.869-2.458 1.109-.906.236-1.939.355-3.072.355h-.73c-.522 0-1.029.188-1.427.525a2.21 2.21 0 0 0-.744 1.328l-.055.299-.924 5.855-.042.215c-.011.068-.03.102-.058.125a.155.155 0 0 1-.096.035H7.266z"
      fill="#253B80"
    />
    <path
      d="M23.048 7.667c-.028.179-.06.362-.096.55-1.237 6.351-5.469 8.545-10.874 8.545H9.326c-.661 0-1.218.48-1.321 1.132L6.596 26.83l-.399 2.533a.704.704 0 0 0 .695.814h4.881c.578 0 1.069-.42 1.16-.99l.048-.248.919-5.832.059-.32c.09-.572.582-.992 1.16-.992h.73c4.729 0 8.431-1.92 9.513-7.476.452-2.321.218-4.259-.978-5.622a4.667 4.667 0 0 0-1.336-1.03z"
      fill="#179BD7"
    />
    <path
      d="M21.754 7.151a9.757 9.757 0 0 0-1.203-.267 15.284 15.284 0 0 0-2.426-.177h-7.352a1.172 1.172 0 0 0-1.159.992L8.05 17.605l-.045.289a1.336 1.336 0 0 1 1.321-1.132h2.752c5.405 0 9.637-2.195 10.874-8.545.037-.188.068-.371.096-.55a6.594 6.594 0 0 0-1.017-.429 9.045 9.045 0 0 0-.277-.087z"
      fill="#222D65"
    />
    <path
      d="M9.614 7.699a1.169 1.169 0 0 1 1.159-.991h7.352c.871 0 1.684.057 2.426.177a9.757 9.757 0 0 1 1.481.353c.365.121.704.264 1.017.429.368-2.347-.003-3.945-1.272-5.392C20.378.682 17.853 0 14.622 0h-9.38c-.66 0-1.223.48-1.325 1.133L.01 25.898a.806.806 0 0 0 .795.932h5.791l1.454-9.225 1.564-9.906z"
      fill="#253B80"
    />
  </svg>
);

const MyFatoorahSVG = (
  <svg
    viewBox="0 0 200 200"
    xmlns="http://www.w3.org/2000/svg"
    className="w-14 h-14"
  >
    <circle cx="100" cy="100" r="100" fill="#00a885" />
    <path
      d="M150 80L100 130L50 80"
      stroke="white"
      strokeWidth="15"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
